import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Users, Clock, MapPin,
  CalendarDays, LayoutGrid, Copy, Loader2, Edit2, Zap, Save, AlertTriangle, PhoneCall
} from 'lucide-react';
import {
  format, startOfWeek, startOfMonth, addDays, addWeeks, subWeeks,
  addMonths, subMonths, isToday
} from 'date-fns';
import { formatTime, formatTimeRange } from '../utils/time';
import { computeMergedHours, shiftsForEmployeeInDates } from '../utils/hours';
import logger from '../utils/logger';
import { useRealtimeEvent } from '../utils/realtime';

// Extracted components
import { SERVICE_CONFIG, SERVICE_STYLES, WEEK_DAYS } from './schedule/constants';
import { toast } from 'sonner';
import DayProgramView from './schedule/DayProgramView';
import OnCallPicker from './schedule/OnCallPicker';
import ParticipantSlotView, { ParticipantPicker } from './schedule/ParticipantSlotView';
import MonthlyCalendar from './schedule/MonthlyCalendar';
import ConflictDialog from './schedule/ConflictDialog';
import OverrideDialog from './schedule/OverrideDialog';
import PrivatePayView from './schedule/PrivatePayView';
import UnassignedShiftsDialog from './schedule/UnassignedShiftsDialog';

export default function ScheduleCalendar() {
  const { api, user } = useAuth();
  const isAdmin = ['administrator', 'supervisor', 'scheduler'].includes(user?.role);
  const isSupervisor = ['administrator', 'supervisor'].includes(user?.role);
  const [viewMode, setViewMode] = useState('weekly');
  const [activeService, setActiveService] = useState('day_program');
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allParticipants, setAllParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [form, setForm] = useState({ employee_id: '', time_in: '09:00', time_out: '16:00', location: '', notes: '', participants: [], group_number: null, date: '', service_line: '', is_draft: true, program_id: '' });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState(null);
  const [dupeOpen, setDupeOpen] = useState(false);
  const [dupeSource, setDupeSource] = useState('');
  const [dupeTarget, setDupeTarget] = useState('');
  const [dupeService, setDupeService] = useState('all');
  const [dupeIsDraft, setDupeIsDraft] = useState(true);
  const [duping, setDuping] = useState(false);
  const [dupeResult, setDupeResult] = useState(null);
  const [shiftPlan, setShiftPlan] = useState({});
  const [planDirty, setPlanDirty] = useState(false);
  const [groupDefaults, setGroupDefaults] = useState({});
  const [onCall, setOnCall] = useState({});
  const [planSaving, setPlanSaving] = useState(false);
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [slotForm, setSlotForm] = useState({ participant_name: '', time_in: '09:00', time_out: '17:00', location: '', notes: '' });
  const [slotSaving, setSlotSaving] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [patternOpen, setPatternOpen] = useState(false);
  const [patternForm, setPatternForm] = useState({ participant_name: '', days: [], time_in: '09:00', time_out: '17:00', location: '', notes: '' });
  const [patternSaving, setPatternSaving] = useState(false);
  const [patternResult, setPatternResult] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSlot, setAssignSlot] = useState(null);
  const [assignSuggestions, setAssignSuggestions] = useState(null);
  const [assignEmpId, setAssignEmpId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [copyDayOpen, setCopyDayOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState('');
  const [copyService, setCopyService] = useState('all');
  const [copyIsDraft, setCopyIsDraft] = useState(true);
  const [copying, setCopying] = useState(false);
  const [copyResult, setCopyResult] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkForm, setBulkForm] = useState({ time_in: '09:00', time_out: '17:00', location: '', notes: '', is_draft: false });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSearch, setBulkSearch] = useState('');
  const [applyingTemplates, setApplyingTemplates] = useState(false);
  const [templateResult, setTemplateResult] = useState(null);
  // Conflict dialog state — stores the 409 detail + a pending action to retry with confirm_conflict=true
  const [conflictState, setConflictState] = useState(null);
  const [conflictBusy, setConflictBusy] = useState(false);
  // 409 auth_overage state — captures pending payload + retry function
  const [overrideState, setOverrideState] = useState(null);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [openShiftsOpen, setOpenShiftsOpen] = useState(false);
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i)), [currentWeek]);
  const weekStart = format(currentWeek, 'yyyy-MM-dd');
  const monthStart = format(currentMonth, 'yyyy-MM-dd');

  // === DATA FETCHING ===
  const [privatePayPrograms, setPrivatePayPrograms] = useState([]);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const shiftParam = viewMode === 'monthly' ? `month_start=${monthStart}` : `week_start=${weekStart}`;
      const [shiftsRes, empRes, partRes, ppRes] = await Promise.all([
        api.get(`/shifts?${shiftParam}`),
        api.get('/employees'),
        api.get('/participants'),
        api.get('/private-pay/programs').catch(() => ({ data: [] })),
      ]);
      setShifts(shiftsRes.data);
      setEmployees(empRes.data);
      setAllParticipants(partRes.data);
      setPrivatePayPrograms(ppRes.data || []);
      if (viewMode === 'monthly') {
        try {
          const planRes = await api.get(`/shift-plan?month=${monthStart.slice(0, 7)}`);
          setShiftPlan(planRes.data?.days || {});
        } catch { setShiftPlan({}); }
      } else {
        // Weekly view needs the plan too — "Shifts needed" overrides (e.g. a 9th
        // Explore group) must apply here. A week can span two months.
        try {
          const months = [...new Set([weekStart.slice(0, 7), format(addDays(new Date(weekStart + 'T12:00:00'), 6), 'yyyy-MM')])];
          const planResults = await Promise.all(months.map(m => api.get(`/shift-plan?month=${m}`)));
          setShiftPlan(Object.assign({}, ...planResults.map(r => r.data?.days || {})));
        } catch { setShiftPlan({}); }
      }
      try {
        const gd = await api.get('/schedule/group-defaults');
        setGroupDefaults(gd.data?.weekday_groups || {});
      } catch { /* non-blocking */ }
      try {
        const start = viewMode === 'monthly' ? monthStart : weekStart;
        const end = format(addDays(new Date(start + 'T12:00:00'), viewMode === 'monthly' ? 35 : 6), 'yyyy-MM-dd');
        const oc = await api.get(`/on-call?start=${start}&end=${end}`);
        setOnCall(Object.fromEntries((oc.data || []).map(d => [d.date, d])));
      } catch { /* non-blocking */ }
    } catch (e) { logger.error('Fetch error:', e); }
    setLoading(false);
  }, [api, weekStart, monthStart, viewMode]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useRealtimeEvent('shifts.changed', fetchData);

  const dayShifts = useMemo(() =>
    shifts.filter(s => (activeService === 'all' || s.service_line === activeService) && s.date === selectedDate)
      .sort((a, b) => (a.group_number || 0) - (b.group_number || 0) || a.time_in.localeCompare(b.time_in)),
    [shifts, activeService, selectedDate]
  );

  const filteredGridEmployees = useMemo(() =>
    employees.filter(e => activeService === 'all' || (e.service_lines || []).includes(activeService) || !(e.service_lines || []).length),
    [employees, activeService]
  );

  // Count drafts in the current view (week or month, filtered by active service line) — drives the Publish All button.
  const draftCount = useMemo(() => {
    return shifts.filter(s => s.is_draft && (activeService === 'all' || s.service_line === activeService)).length;
  }, [shifts, activeService]);

  // Count of unassigned shifts in the current view — drives the "Open Shifts" toolbar button.
  const unassignedCount = useMemo(() => {
    return shifts.filter(s => (!s.employee_id || s.employee_id === '')
      && (activeService === 'all' || s.service_line === activeService)).length;
  }, [shifts, activeService]);

  // === SHIFT CRUD ===
  const openAdd = async (groupNumber) => {
    const svc = activeService === 'all' ? 'day_program' : activeService;
    const cfg = SERVICE_CONFIG[svc];
    setEditingShift(null);
    setForm({ employee_id: '', time_in: cfg.fixedTime ? cfg.timeIn : '09:00', time_out: cfg.fixedTime ? cfg.timeOut : '17:00', location: '', notes: '', participants: [], group_number: groupNumber, date: selectedDate, service_line: svc, is_draft: !isSupervisor });
    if (activeService === 'all') setActiveService('day_program');
    try {
      const timeIn = cfg.fixedTime ? cfg.timeIn : '09:00';
      const timeOut = cfg.fixedTime ? cfg.timeOut : '17:00';
      const { data } = await api.get(`/staff/suggest?date=${selectedDate}&service_line=${svc}&week_start=${weekStart}&time_in=${timeIn}&time_out=${timeOut}`);
      setSuggestions(data);
    } catch { setSuggestions(null); }
    setDialogOpen(true);
  };

  const openEdit = (shift) => {
    setEditingShift(shift);
    setForm({ employee_id: shift.employee_id, time_in: shift.time_in, time_out: shift.time_out, location: shift.location || '', notes: shift.notes || '', participants: shift.participants || [], group_number: shift.group_number, date: shift.date, service_line: shift.service_line, is_draft: !!shift.is_draft, program_id: shift.program_id || '' });
    setDialogOpen(true);
  };

  // === PRIVATE PAY HANDLERS ===
  const openAddPrivatePaySession = (programId) => {
    const prog = privatePayPrograms.find(p => p.id === programId);
    setEditingShift(null);
    setForm({
      employee_id: '',
      time_in: prog?.default_time_in || '17:00',
      time_out: prog?.default_time_out || '20:00',
      location: prog?.location || '',
      notes: '',
      participants: [],
      group_number: null,
      date: selectedDate,
      service_line: 'private_pay',
      is_draft: !isSupervisor,
      program_id: programId || '',
    });
    setSuggestions(null);
    setDialogOpen(true);
  };
  const assignPrivatePayStaff = (sess) => {
    setEditingShift(null);
    setForm({
      employee_id: '',
      time_in: sess.time_in,
      time_out: sess.time_out,
      location: sess.location || '',
      notes: '',
      participants: [],
      group_number: null,
      date: selectedDate,
      service_line: 'private_pay',
      is_draft: !isSupervisor,
      program_id: sess.program_id || '',
    });
    setDialogOpen(true);
  };
  const unassignPrivatePayStaff = async (shift) => {
    if (!window.confirm('Remove this staff from the session?')) return;
    try { await api.delete(`/shifts/${shift.id}`); fetchData(); } catch { /* */ }
  };
  const editPrivatePaySession = (sess) => {
    if (sess.staff_shifts.length > 0) openEdit(sess.staff_shifts[0]);
    else openAddPrivatePaySession(sess.program_id);
  };
  const deletePrivatePaySession = async (sess) => {
    if (!window.confirm(`Delete this entire session and all ${sess.staff_shifts.length} staff assignments?`)) return;
    try {
      await Promise.all(sess.staff_shifts.map(sh => api.delete(`/shifts/${sh.id}`)));
      fetchData();
    } catch { /* */ }
  };

  const toggleParticipant = (name) => {
    if (name === 'clear') { setForm(p => ({ ...p, participants: [] })); return; }
    setForm(p => ({ ...p, participants: p.participants.includes(name) ? p.participants.filter(n => n !== name) : [...p.participants, name] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const svc = form.service_line || (activeService === 'all' ? 'day_program' : activeService);
      const payload = { employee_id: form.employee_id, service_line: svc, date: form.date, time_in: form.time_in, time_out: form.time_out, location: form.location, notes: form.notes, participants: form.participants, group_number: form.group_number, is_draft: !!form.is_draft, program_id: form.program_id || '' };
      const url = editingShift ? `/shifts/${editingShift.id}` : '/shifts';
      const method = editingShift ? 'put' : 'post';
      try {
        await api[method](url, payload);
      } catch (err) {
        if (err.response?.status === 409 && err.response?.data?.detail?.code === 'shift_conflict') {
          const d = err.response.data.detail;
          setSaving(false);
          setConflictState({
            ...d,
            onConfirm: async () => {
              setConflictBusy(true);
              try {
                await api[method](`${url}?confirm_conflict=true`, payload);
                setConflictState(null);
                setDialogOpen(false);
                fetchData();
              } catch (e2) {
                // Could chain into auth_overage if conflict resolved but auth still over
                if (e2.response?.status === 409 && e2.response?.data?.detail?.code === 'auth_overage') {
                  setConflictState(null);
                  setOverrideState({
                    breaches: e2.response.data.detail.breaches || [],
                    onConfirm: async (reason) => {
                      setOverrideBusy(true);
                      try {
                        await api[method](`${url}?confirm_conflict=true`, { ...payload, override_reason: reason });
                        setOverrideState(null);
                        setDialogOpen(false);
                        fetchData();
                      } catch (e3) {
                        alert(e3.response?.data?.detail?.message || e3.response?.data?.detail || 'Error');
                      } finally {
                        setOverrideBusy(false);
                      }
                    },
                  });
                } else {
                  alert(e2.response?.data?.detail?.message || e2.response?.data?.detail || 'Error');
                }
              } finally {
                setConflictBusy(false);
              }
            },
          });
          return;
        }
        if (err.response?.status === 409 && err.response?.data?.detail?.code === 'auth_overage') {
          setSaving(false);
          setOverrideState({
            breaches: err.response.data.detail.breaches || [],
            onConfirm: async (reason) => {
              setOverrideBusy(true);
              try {
                await api[method](url, { ...payload, override_reason: reason });
                setOverrideState(null);
                setDialogOpen(false);
                fetchData();
              } catch (e2) {
                alert(e2.response?.data?.detail?.message || e2.response?.data?.detail || 'Error');
              } finally {
                setOverrideBusy(false);
              }
            },
          });
          return;
        }
        throw err;
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail?.message || e.response?.data?.detail || 'Error saving shift'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!editingShift || !window.confirm('Delete this shift?')) return;
    try { await api.delete(`/shifts/${editingShift.id}`); setDialogOpen(false); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Error deleting'); }
  };

  const handlePublishOne = async () => {
    if (!editingShift) return;
    setPublishing(true);
    try {
      await api.post(`/shifts/${editingShift.id}/publish`);
      setDialogOpen(false);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Publish failed'); }
    setPublishing(false);
  };

  const handleBulkPublish = async () => {
    const range = viewMode === 'monthly'
      ? { date_start: format(startOfMonth(currentMonth), 'yyyy-MM-dd'), date_end: format(addDays(addMonths(currentMonth, 1), -1), 'yyyy-MM-dd'), label: format(currentMonth, 'MMMM yyyy') }
      : { date_start: weekStart, date_end: format(addDays(currentWeek, 6), 'yyyy-MM-dd'), label: `the week of ${format(currentWeek, 'MMM d')}` };
    const svcLabel = activeService === 'all' ? 'all service lines' : (SERVICE_CONFIG[activeService]?.label || activeService);
    if (!window.confirm(`Publish ALL draft shifts in ${range.label} (${svcLabel})? Staff will see them immediately.`)) return;
    setBulkPublishing(true);
    try {
      const body = { date_start: range.date_start, date_end: range.date_end };
      if (activeService !== 'all') body.service_line = activeService;
      const { data } = await api.post('/shifts/publish-bulk', body);
      alert(`Published ${data.published} shift${data.published === 1 ? '' : 's'}.`);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Bulk publish failed'); }
    setBulkPublishing(false);
  };

  const handleBatchDelete = async (shiftIds) => {
    if (!window.confirm(`Delete ${shiftIds.length} selected shift${shiftIds.length > 1 ? 's' : ''}?`)) return;
    try { await Promise.all(shiftIds.map(id => api.delete(`/shifts/${id}`))); fetchData(); } catch (e) { logger.error(e); }
  };

  const handleSwapGroups = async (sourceShift, targetShift, sourceGroup, targetGroup) => {
    try {
      if (targetShift) {
        // Atomic swap: bypasses same-time overlap guard.
        await api.post('/shifts/swap', { shift_a_id: sourceShift.id, shift_b_id: targetShift.id });
      } else {
        // Target group is empty — simple group_number update.
        await api.put(`/shifts/${sourceShift.id}`, { group_number: targetGroup });
      }
      fetchData();
    } catch (e) {
      logger.error(e);
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || 'Swap failed';
      alert(typeof msg === 'string' ? msg : 'Swap failed');
    }
  };

  const handleReplaceStaff = async (shift, newEmployeeId) => {
    try {
      await api.put(`/shifts/${shift.id}?confirm_conflict=true`, { employee_id: newEmployeeId });
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to replace staff';
      alert(typeof msg === 'string' ? msg : 'Failed to replace staff');
    }
  };

  const handleSetOnCall = async (employeeId) => {
    try {
      const { data } = await api.put(`/on-call/${selectedDate}`, { employee_id: employeeId });
      setOnCall(prev => {
        const next = { ...prev };
        if (data.cleared) delete next[selectedDate]; else next[selectedDate] = data;
        return next;
      });
      toast.success(data.cleared ? 'On-call cleared for this day' : `${data.employee_name} is on call ${data.time_in}–${data.time_out}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set on-call'); }
  };

  const availabilityFetcher = useMemo(() => async ({ shift, q }) => {
    try {
      const params = new URLSearchParams({
        date: shift.date,
        service_line: shift.service_line || 'day_program',
        time_in: shift.time_in || '09:00',
        time_out: shift.time_out || '16:00',
        exclude_shift_id: shift.id,
      });
      if (q) params.append('q', q);
      const { data } = await api.get(`/staff/suggest?${params.toString()}`);
      return data.suggestions || [];
    } catch { return []; }
  }, [api]);

  const quickAddHandlers = useMemo(() => ({
    search: async (query) => {
      try { const { data } = await api.get(`/staff/search?q=${encodeURIComponent(query)}&service_line=day_program`); return data; } catch { return []; }
    },
    assign: async (employeeId, groupNumber) => {
      const payload = { employee_id: employeeId, service_line: 'day_program', date: selectedDate, time_in: '09:00', time_out: '16:00', group_number: groupNumber };
      try {
        await api.post('/shifts', payload);
        fetchData();
      } catch (e) {
        if (e.response?.status === 409 && e.response?.data?.detail?.code === 'shift_conflict') {
          const d = e.response.data.detail;
          setConflictState({
            ...d,
            onConfirm: async () => {
              setConflictBusy(true);
              try {
                await api.post('/shifts?confirm_conflict=true', payload);
                setConflictState(null);
                fetchData();
              } catch (e2) {
                alert(e2.response?.data?.detail?.message || e2.response?.data?.detail || 'Error');
              } finally {
                setConflictBusy(false);
              }
            },
          });
        } else {
          alert(e.response?.data?.detail?.message || e.response?.data?.detail || 'Error');
        }
      }
    }
  }), [api, selectedDate, fetchData]);

  // === COPY / DUPE / AUTOFILL ===
  const handleCopyDay = async () => {
    setCopying(true); setCopyResult(null);
    try { const { data } = await api.post('/shifts/copy-day', { source_date: selectedDate, target_date: copyTarget, service_line: copyService === 'all' ? null : copyService, is_draft: !!copyIsDraft }); setCopyResult(data); fetchData(); } catch (e) { setCopyResult({ error: e.response?.data?.detail || 'Copy failed' }); }
    setCopying(false);
  };

  const handleAutoFill = async () => {
    setAutoFilling(true); setAutoFillResult(null);
    try { const svc = activeService === 'all' ? 'day_program' : activeService; const { data } = await api.post(`/shifts/auto-fill?date=${selectedDate}&service_line=${svc}`); setAutoFillResult(data); fetchData(); setTimeout(() => setAutoFillResult(null), 5000); } catch (e) { setAutoFillResult({ error: e.response?.data?.detail || 'Auto-fill failed' }); }
    setAutoFilling(false);
  };

  const handleApplyTemplates = async () => {
    setApplyingTemplates(true); setTemplateResult(null);
    try {
      const svc = activeService === 'all' ? null : activeService;
      const body = { week_start: weekStart };
      if (svc) body.service_line = svc;
      const { data } = await api.post('/participants/apply-templates', body);
      setTemplateResult(data);
      fetchData();
      setTimeout(() => setTemplateResult(null), 8000);
    } catch (e) { setTemplateResult({ error: e.response?.data?.detail || 'Apply templates failed' }); }
    setApplyingTemplates(false);
  };

  const handleDuplicateWeek = async () => {
    if (!dupeSource || !dupeTarget) return;
    setDuping(true); setDupeResult(null);
    try { const { data } = await api.post('/shifts/duplicate-week', { source_week_start: dupeSource, target_week_start: dupeTarget, service_line: dupeService === 'all' ? null : dupeService, is_draft: !!dupeIsDraft }); setDupeResult(data); fetchData(); } catch (e) { setDupeResult({ error: e.response?.data?.detail || 'Failed' }); }
    setDuping(false);
  };

  const updatePlanSlots = (date, service, value) => { setShiftPlan(prev => ({ ...prev, [date]: { ...(prev[date] || {}), [service]: parseInt(value) || 0 } })); setPlanDirty(true); };
  const savePlan = async () => { setPlanSaving(true); try { await api.put('/shift-plan', { month: format(currentMonth, 'yyyy-MM'), days: shiftPlan }); setPlanDirty(false); } catch (e) { logger.error(e); } setPlanSaving(false); };

  const exploreSlotsFor = useCallback((date) => {
    const override = shiftPlan[date]?.day_program;
    if (override !== undefined && override !== '' && override !== null) {
      const n = parseInt(override);
      if (!isNaN(n)) return n;
    }
    const dow = new Date(date + 'T12:00:00').getDay(); // 0=Sun, 6=Sat
    const wd = groupDefaults[['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dow]];
    if (wd !== undefined && wd !== null && wd !== '') {
      const n = parseInt(wd);
      if (!isNaN(n)) return n;
    }
    return (dow === 0 || dow === 6) ? 0 : 8;
  }, [shiftPlan, groupDefaults]);

  const handleAddGroup = async () => {
    const next = exploreSlotsFor(selectedDate) + 1;
    const month = selectedDate.slice(0, 7);
    const dayName = new Date(selectedDate + 'T12:00:00').toLocaleDateString([], { weekday: 'long' });
    try {
      const { data: plan } = await api.get(`/shift-plan?month=${month}`);
      const days = { ...(plan?.days || {}), [selectedDate]: { ...((plan?.days || {})[selectedDate] || {}), day_program: next } };
      await api.put('/shift-plan', { month, days });
      setShiftPlan(prev => ({ ...prev, [selectedDate]: { ...(prev[selectedDate] || {}), day_program: next } }));
      toast.success(`Group ${next} added for this ${dayName}`, {
        duration: 8000,
        action: {
          label: `Every ${dayName}`,
          onClick: async () => {
            const dk = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][new Date(selectedDate + 'T12:00:00').getDay()];
            try {
              await api.put('/schedule/group-defaults', { weekday_groups: { ...groupDefaults, [dk]: next } });
              setGroupDefaults(g => ({ ...g, [dk]: next }));
              toast.success(`All ${dayName}s now default to ${next} Explore groups`);
            } catch (e) { toast.error('Failed to save default'); }
          },
        },
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add group'); }
  };

  // === PARTICIPANT-FIRST SCHEDULING ===
  const openAddSlot = () => { setEditingSlot(null); setSlotForm({ participant_name: '', time_in: '09:00', time_out: '17:00', location: '', notes: '', is_draft: !isSupervisor }); setSlotDialogOpen(true); };
  const openEditSlot = (slot) => { setEditingSlot(slot); setSlotForm({ participant_name: (slot.participants || [])[0] || '', time_in: slot.time_in, time_out: slot.time_out, location: slot.location || '', notes: slot.notes || '', is_draft: !!slot.is_draft }); setSlotDialogOpen(true); };

  const handleSaveSlot = async () => {
    setSlotSaving(true);
    try {
      const svc = activeService === 'all' ? 'employment' : activeService;
      const name = (slotForm.participant_name || '').trim();
      if (!name) { setSlotSaving(false); return; }
      // Auto-create participant if they don't exist yet (case-insensitive match)
      const existing = allParticipants.find(p => (p.name || '').trim().toLowerCase() === name.toLowerCase());
      if (!existing && !editingSlot) {
        try {
          await api.post('/participants', { name, service_lines: [svc], notes: '' });
        } catch (err) {
          // Non-fatal: still try to create the slot. Backend allows free-form names.
          logger.error('auto-create participant failed', err);
        }
      }
      if (editingSlot) { await api.put(`/shifts/${editingSlot.id}`, { participants: [name], time_in: slotForm.time_in, time_out: slotForm.time_out, location: slotForm.location, notes: slotForm.notes, is_draft: !!slotForm.is_draft }); }
      else { await api.post('/participant-slots', { participant_name: name, service_line: svc, date: selectedDate, time_in: slotForm.time_in, time_out: slotForm.time_out, location: slotForm.location, notes: slotForm.notes, is_draft: !!slotForm.is_draft }); }
      setSlotDialogOpen(false); fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSlotSaving(false);
  };

  const handleDeleteSlot = async () => { if (!editingSlot || !window.confirm('Delete?')) return; try { await api.delete(`/shifts/${editingSlot.id}`); setSlotDialogOpen(false); fetchData(); } catch (e) { logger.error(e); } };

  const openWeeklyPattern = () => { setPatternForm({ participant_name: '', days: [], time_in: '09:00', time_out: '17:00', location: '', notes: '', is_draft: !isSupervisor }); setPatternResult(null); setPatternOpen(true); };
  const togglePatternDay = (day) => { setPatternForm(p => ({ ...p, days: p.days.includes(day) ? p.days.filter(d => d !== day) : [...p.days, day] })); };

  const handleCreatePattern = async () => {
    setPatternSaving(true); setPatternResult(null);
    try {
      const svc = activeService === 'all' ? 'employment' : activeService;
      const name = (patternForm.participant_name || '').trim();
      if (!name) { setPatternSaving(false); return; }
      const existing = allParticipants.find(p => (p.name || '').trim().toLowerCase() === name.toLowerCase());
      if (!existing) {
        try { await api.post('/participants', { name, service_lines: [svc], notes: '' }); }
        catch (err) { logger.error('auto-create participant failed', err); }
      }
      const { data } = await api.post('/participant-slots/weekly-pattern', { participant_name: name, service_line: svc, week_start: weekStart, days: patternForm.days, time_in: patternForm.time_in, time_out: patternForm.time_out, location: patternForm.location, notes: patternForm.notes, is_draft: !!patternForm.is_draft });
      setPatternResult(data); fetchData();
    } catch (e) { setPatternResult({ error: e.response?.data?.detail || 'Error' }); }
    setPatternSaving(false);
  };

  const openAssignStaff = async (slot) => {
    setAssignSlot(slot); setAssignEmpId('');
    try { const svc = activeService === 'all' ? slot.service_line : activeService; const { data } = await api.get(`/staff/suggest?date=${slot.date}&service_line=${svc}&week_start=${weekStart}&time_in=${slot.time_in}&time_out=${slot.time_out}`); setAssignSuggestions(data); } catch { setAssignSuggestions(null); }
    setAssignOpen(true);
  };

  const handleAssignStaff = async () => {
    if (!assignSlot || !assignEmpId) return;
    setAssigning(true);
    try {
      await api.put(`/participant-slots/${assignSlot.id}/assign`, { employee_id: assignEmpId });
      setAssignOpen(false);
      fetchData();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.code === 'shift_conflict') {
        const d = e.response.data.detail;
        setConflictState({
          ...d,
          onConfirm: async () => {
            setConflictBusy(true);
            try {
              await api.put(`/participant-slots/${assignSlot.id}/assign?confirm_conflict=true`, { employee_id: assignEmpId });
              setConflictState(null);
              setAssignOpen(false);
              fetchData();
            } catch (e2) {
              alert(e2.response?.data?.detail?.message || e2.response?.data?.detail || 'Error');
            } finally {
              setConflictBusy(false);
            }
          },
        });
      } else {
        alert(e.response?.data?.detail?.message || e.response?.data?.detail || 'Error');
      }
    }
    setAssigning(false);
  };

  const handleUnassignStaff = async (shiftId) => { if (!window.confirm('Remove staff?')) return; try { await api.put(`/participant-slots/${shiftId}/unassign`); fetchData(); } catch (e) { logger.error(e); } };

  const toggleBulkParticipant = (name) => { setBulkSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]); };
  const handleBulkCreate = async () => {
    setBulkSaving(true); setBulkResult(null);
    try { const svc = activeService === 'all' ? 'employment' : activeService; const { data } = await api.post('/participant-slots/bulk', { participant_names: bulkSelected, service_line: svc, date: selectedDate, time_in: bulkForm.time_in, time_out: bulkForm.time_out, location: bulkForm.location, notes: bulkForm.notes, is_draft: !!bulkForm.is_draft }); setBulkResult(data); fetchData(); } catch (e) { setBulkResult({ error: e.response?.data?.detail || 'Error' }); }
    setBulkSaving(false);
  };

  // === NAVIGATION ===
  const goToday = () => { const t = new Date(); setCurrentWeek(startOfWeek(t, { weekStartsOn: 0 })); setCurrentMonth(startOfMonth(t)); setSelectedDate(format(t, 'yyyy-MM-dd')); };
  const goPrev = () => { if (viewMode === 'monthly') setCurrentMonth(subMonths(currentMonth, 1)); else setCurrentWeek(subWeeks(currentWeek, 1)); };
  const goNext = () => { if (viewMode === 'monthly') setCurrentMonth(addMonths(currentMonth, 1)); else setCurrentWeek(addWeeks(currentWeek, 1)); };
  const headerText = viewMode === 'monthly' ? format(currentMonth, 'MMMM yyyy') : `Week of ${format(currentWeek, 'MMM d')} - ${format(addDays(currentWeek, 6), 'MMM d, yyyy')}`;

  const dialogSvc = form.service_line || (activeService === 'all' ? 'day_program' : activeService);
  const dialogConfig = SERVICE_CONFIG[dialogSvc];

  // === RENDER ===
  return (
    <div className="p-6 sm:p-8 space-y-5" data-testid="schedule-calendar">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Schedule</h1>
          <p className="text-sm text-[#8E85A0] mt-1">{headerText}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={handleAutoFill} disabled={autoFilling}
                className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1.5" data-testid="auto-fill-btn">
                {autoFilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />} Auto-Fill
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setCopyTarget(''); setCopyService('all'); setCopyIsDraft(!isSupervisor); setCopyResult(null); setCopyDayOpen(true); }}
                className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1.5" data-testid="copy-day-btn">
                <Copy className="h-3.5 w-3.5" /> Copy Day
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setDupeSource(weekStart); setDupeTarget(format(addWeeks(currentWeek, 1), 'yyyy-MM-dd')); setDupeService('all'); setDupeIsDraft(!isSupervisor); setDupeResult(null); setDupeOpen(true); }}
                className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1.5" data-testid="duplicate-week-btn">
                <Copy className="h-3.5 w-3.5" /> Dup Week
              </Button>
              <Button variant="outline" size="sm" onClick={handleApplyTemplates} disabled={applyingTemplates}
                className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1.5" data-testid="apply-templates-btn">
                {applyingTemplates ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarDays className="h-3.5 w-3.5" />} Templates
              </Button>
              <Button variant="outline" size="sm" onClick={() => setOpenShiftsOpen(true)}
                className={`gap-1.5 font-semibold ${unassignedCount > 0
                  ? 'border-[#E65100] text-[#E65100] hover:bg-[#FFF3E0]'
                  : 'border-[#DED8EB] text-[#8E85A0] hover:bg-[#F0EDF6]'}`}
                data-testid="open-shifts-btn">
                <AlertTriangle className="h-3.5 w-3.5" />
                Open Shifts
                {unassignedCount > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#E65100] text-white text-[10px] font-bold" data-testid="open-shifts-badge">
                    {unassignedCount}
                  </span>
                )}
              </Button>
              {isSupervisor && draftCount > 0 && (
                <Button variant="outline" size="sm" onClick={handleBulkPublish} disabled={bulkPublishing}
                  className="border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] gap-1.5 font-semibold" data-testid="bulk-publish-btn">
                  {bulkPublishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : '🚀'} Publish {draftCount} draft{draftCount === 1 ? '' : 's'}
                </Button>
              )}
            </>
          )}
          <div className="flex items-center bg-[#F0EDF6] border border-[#DED8EB] rounded-lg p-0.5" data-testid="view-toggle">
            {[['weekly', CalendarDays, 'Weekly'], ['grid', LayoutGrid, 'Grid'], ['monthly', CalendarDays, 'Monthly']].map(([mode, Icon, label]) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === mode ? 'bg-white text-[#2D1B4E] shadow-sm' : 'text-[#8E85A0] hover:text-[#5A4D6E]'}`}
                data-testid={`view-${mode}-btn`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={goPrev} className="border-[#DED8EB] h-8 w-8" data-testid="prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={goToday} className="border-[#DED8EB] text-[#2D1B4E] h-8" data-testid="today-btn">Today</Button>
            <Button variant="outline" size="icon" onClick={goNext} className="border-[#DED8EB] h-8 w-8" data-testid="next-btn"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Auto-fill result */}
      {autoFillResult && (
        <div className={`p-3 rounded-lg border text-sm ${autoFillResult.error ? 'bg-red-50 border-red-200 text-[#D9534F]' : 'bg-[#E8F5E9] border-[#81C784] text-[#1B5E20]'}`} data-testid="auto-fill-result">
          {autoFillResult.error ? autoFillResult.error : autoFillResult.created > 0 ? <>Assigned <strong>{autoFillResult.created}</strong> staff: {autoFillResult.staff_assigned?.join(', ')} ({autoFillResult.total_filled}/{autoFillResult.slots_needed} filled)</> : autoFillResult.message || 'No changes needed'}
        </div>
      )}

      {/* Template result */}
      {templateResult && (
        <div className={`p-3 rounded-lg border text-sm ${templateResult.error ? 'bg-red-50 border-red-200 text-[#D9534F]' : 'bg-[#EDE7F6] border-[#9B7ED8] text-[#2D1B4E]'}`} data-testid="template-result">
          {templateResult.error ? templateResult.error : templateResult.created > 0
            ? <>Applied templates: <strong>{templateResult.created}</strong> shifts created from {templateResult.participants_processed} participants ({templateResult.skipped} skipped — already scheduled)</>
            : `No new shifts created (${templateResult.skipped} already scheduled from ${templateResult.participants_processed} participants)`}
        </div>
      )}

      {/* === MONTHLY VIEW === */}
      {viewMode === 'monthly' && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Select value={activeService} onValueChange={setActiveService}>
                <SelectTrigger className="w-52 border-[#DED8EB] bg-white" data-testid="month-service-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Service Lines</SelectItem>
                  <SelectItem value="day_program">Explore Program</SelectItem>
                  <SelectItem value="employment">Employment</SelectItem>
                  <SelectItem value="in_home">IHCS</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8E85A0]">Click any day to view & schedule</p>
            </div>
            {isAdmin && planDirty && (
              <Button size="sm" onClick={savePlan} disabled={planSaving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="save-plan-btn">
                {planSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Shift Plan
              </Button>
            )}
          </div>

          <MonthlyCalendar currentMonth={currentMonth} shifts={shifts} activeService={activeService} onDayClick={(d) => setSelectedDate(format(d, 'yyyy-MM-dd'))} selectedDate={selectedDate} onCall={onCall} />

          {/* Day detail panel */}
          {selectedDate && (
            <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="month-day-detail">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {format(new Date(selectedDate + 'T12:00:00'), 'EEEE, MMMM d, yyyy')}
                  </h3>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={handleAutoFill} disabled={autoFilling} className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1" data-testid="month-auto-fill-btn">
                        {autoFilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />} Auto-Fill
                      </Button>
                      <Button size="sm" onClick={() => openAdd(null)} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="month-add-shift-btn">
                        <Plus className="h-3.5 w-3.5" /> Add Shift
                      </Button>
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-4 mb-4 p-3 rounded-lg bg-[#F0EDF6] border border-[#DED8EB]" data-testid="day-plan-inputs">
                    <span className="text-xs font-semibold text-[#5A4D6E] uppercase tracking-wider shrink-0">Shifts needed:</span>
                    {Object.entries(SERVICE_CONFIG).map(([key, cfg]) => {
                      const planned = shiftPlan[selectedDate]?.[key] || '';
                      // Count DISTINCT assigned staff only (skip empty employee_id, dedupe duplicates across groups)
                      const filled = new Set(
                        shifts
                          .filter(s => s.date === selectedDate && s.service_line === key && s.employee_id)
                          .map(s => s.employee_id)
                      ).size;
                      return (
                        <div key={key} className="flex items-center gap-1.5">
                          <label className="text-xs text-[#5A4D6E]">{cfg.label}:</label>
                          <Input type="number" value={planned} onChange={e => updatePlanSlots(selectedDate, key, e.target.value)}
                            placeholder={String(cfg.maxGroups || cfg.maxSlots || 8)}
                            className="border-[#DED8EB] bg-white h-7 w-14 text-xs text-center" data-testid={`plan-${key}-${selectedDate}`} />
                          <span className={`text-[10px] font-mono ${filled >= (planned || 0) && planned ? 'text-[#2E7D32]' : 'text-[#8E85A0]'}`}>{filled} filled</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {dayShifts.length === 0 ? (
                  <p className="text-sm text-[#8E85A0] text-center py-4">No {activeService !== 'all' ? (SERVICE_CONFIG[activeService]?.label + ' ') : ''}shifts on this day</p>
                ) : (
                  <div className="space-y-1.5">
                    {dayShifts.map(shift => (
                      <div key={shift.id} onClick={() => isAdmin && openEdit(shift)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:-translate-y-px hover:shadow-sm ${SERVICE_STYLES[shift.service_line] || 'border-[#DED8EB]'}`}
                        data-testid={`month-shift-${shift.id}`}>
                        <span className="text-sm font-semibold shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(shift.time_in, shift.time_out)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{shift.employee_name}</p>
                          {(shift.participants || []).length > 0 && <p className="text-xs opacity-75 truncate">{shift.participants.join(', ')}</p>}
                        </div>
                        <Badge className={`text-[10px] shrink-0 ${SERVICE_STYLES[shift.service_line]}`}>
                          {SERVICE_CONFIG[shift.service_line]?.label}{shift.group_number ? ` G${shift.group_number}` : ''}
                        </Badge>
                        {shift.location && <span className="text-xs opacity-75 shrink-0 flex items-center gap-0.5"><MapPin className="h-3 w-3" />{shift.location}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* === WEEKLY VIEW === */}
      {viewMode === 'weekly' && (
        <>
          <div className="flex gap-2" data-testid="day-selector">
            {weekDates.map(date => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const isSelected = dateStr === selectedDate;
              const today = isToday(date);
              const dayCount = activeService === 'day_program'
                ? new Set(
                    shifts
                      .filter(s => s.date === dateStr && s.service_line === activeService && s.group_number)
                      .map(s => s.group_number)
                  ).size
                : new Set(
                    shifts
                      .filter(s => s.date === dateStr && s.service_line === activeService && s.employee_id)
                      .map(s => s.employee_id)
                  ).size;
              const countLabel = activeService === 'day_program' ? 'groups' : 'staff';
              return (
                <button key={dateStr} onClick={() => setSelectedDate(dateStr)}
                  className={`flex-1 p-3 rounded-lg border text-center transition-all ${
                    isSelected ? 'border-[#6B3FA0] bg-[#6B3FA0] text-white shadow-sm' :
                    today ? 'border-[#6B3FA0] bg-white text-[#2D1B4E]' :
                    'border-[#DED8EB] bg-white text-[#5A4D6E] hover:border-[#9B7ED8]'
                  }`} data-testid={`day-btn-${dateStr}`}>
                  <p className="text-xs font-semibold uppercase">{WEEK_DAYS[date.getDay()]}</p>
                  <p className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{format(date, 'd')}</p>
                  {dayCount > 0 && <p className={`text-[10px] font-medium mt-0.5 ${isSelected ? 'text-white/80' : 'text-[#8E85A0]'}`}>{dayCount} {countLabel}</p>}
                </button>
              );
            })}
          </div>
          <div className="mb-3 flex items-center gap-3 flex-wrap rounded-lg border border-[#DED8EB] bg-gradient-to-r from-[#F7F4FB] to-white px-3 py-2" data-testid="oncall-bar">
            <span className="text-xs font-bold uppercase tracking-wider text-[#6B3FA0] flex items-center gap-1.5">
              <PhoneCall className="h-3.5 w-3.5" /> On call
            </span>
            {isAdmin ? (
              <OnCallPicker date={selectedDate} current={onCall[selectedDate]}
                onAssign={(id) => handleSetOnCall(id)} onClear={() => handleSetOnCall('')} />
            ) : (
              <span className="text-sm font-semibold text-[#2D1B4E]" data-testid="oncall-name">
                {onCall[selectedDate]?.employee_name || 'No one assigned'}
              </span>
            )}
            {onCall[selectedDate] && (
              <span className="text-xs text-[#8E85A0]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {formatTimeRange(onCall[selectedDate].time_in, onCall[selectedDate].time_out)} · counts toward weekly hours · first pick for call-out cover
              </span>
            )}
          </div>
          <Tabs value={activeService} onValueChange={setActiveService}>
            <TabsList className="bg-[#F0EDF6] border border-[#DED8EB]" data-testid="service-tabs">
              {Object.entries(SERVICE_CONFIG).map(([key, cfg]) => (
                <TabsTrigger key={key} value={key} className="data-[state=active]:bg-white gap-1.5" data-testid={`tab-${key}`}>
                  {cfg.label}
                  <Badge className={`text-[10px] h-5 min-w-5 flex items-center justify-center rounded-full ${activeService === key ? 'bg-[#6B3FA0] text-white' : 'bg-[#DED8EB] text-[#5A4D6E]'}`}>
                    {key === 'day_program'
                      ? new Set(
                          shifts
                            .filter(s => s.date === selectedDate && s.service_line === key && s.group_number)
                            .map(s => s.group_number)
                        ).size
                      : new Set(
                          shifts
                            .filter(s => s.date === selectedDate && s.service_line === key && s.employee_id)
                            .map(s => s.employee_id)
                        ).size}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="day_program" className="mt-4">
              <DayProgramView shifts={dayShifts} date={selectedDate} onAdd={openAdd} onEdit={openEdit} onSwapGroups={handleSwapGroups} onBatchDelete={handleBatchDelete} onQuickAdd={quickAddHandlers} onReplaceStaff={handleReplaceStaff} onAutoFillDay={handleAutoFill} autoFilling={autoFilling} availabilityFetcher={availabilityFetcher}
                onAddGroup={isAdmin ? handleAddGroup : undefined}
                slotsNeeded={exploreSlotsFor(selectedDate)}
              />
            </TabsContent>
            <TabsContent value="employment" className="mt-4">
              <ParticipantSlotView shifts={dayShifts} date={selectedDate} serviceLine="employment"
                onEdit={openEditSlot} onAssign={openAssignStaff} onUnassign={handleUnassignStaff}
                onAddSlot={openAddSlot} onAddPattern={openWeeklyPattern} onBulkAdd={() => { setBulkSelected([]); setBulkSearch(''); setBulkForm({ time_in: '09:00', time_out: '17:00', location: '', notes: '', is_draft: !isSupervisor }); setBulkResult(null); setBulkOpen(true); }}
                allParticipants={allParticipants} weekStart={weekStart} />
            </TabsContent>
            <TabsContent value="in_home" className="mt-4">
              <ParticipantSlotView shifts={dayShifts} date={selectedDate} serviceLine="in_home"
                onEdit={openEditSlot} onAssign={openAssignStaff} onUnassign={handleUnassignStaff}
                onAddSlot={openAddSlot} onAddPattern={openWeeklyPattern} onBulkAdd={() => { setBulkSelected([]); setBulkSearch(''); setBulkForm({ time_in: '09:00', time_out: '17:00', location: '', notes: '', is_draft: !isSupervisor }); setBulkResult(null); setBulkOpen(true); }}
                allParticipants={allParticipants} weekStart={weekStart} />
            </TabsContent>
            <TabsContent value="private_pay" className="mt-4">
              <PrivatePayView
                shifts={dayShifts}
                date={selectedDate}
                programs={privatePayPrograms}
                employees={employees}
                onAddSession={openAddPrivatePaySession}
                onEditSession={editPrivatePaySession}
                onAssignStaff={assignPrivatePayStaff}
                onUnassignStaff={unassignPrivatePayStaff}
                onDeleteSession={deletePrivatePaySession}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* === GRID VIEW === */}
      {viewMode === 'grid' && (
        <div data-testid="week-grid-view">
          <div className="flex items-center gap-2 mb-3">
            <Select value={activeService} onValueChange={setActiveService}>
              <SelectTrigger className="w-52 border-[#DED8EB] bg-white" data-testid="grid-service-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Service Lines</SelectItem>
                <SelectItem value="day_program">Day Program</SelectItem>
                <SelectItem value="employment">Employment</SelectItem>
                <SelectItem value="in_home">IHCS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="border border-[#DED8EB] rounded-lg overflow-hidden bg-white">
            <div className="grid grid-cols-8 bg-[#F0EDF6] border-b border-[#DED8EB]">
              <div className="px-3 py-2 text-xs font-semibold text-[#8E85A0] uppercase tracking-wider border-r border-[#DED8EB]">Staff</div>
              {weekDates.map(date => {
                const dateStr = format(date, 'yyyy-MM-dd');
                const today = isToday(date);
                return (
                  <div key={dateStr} className={`px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider border-r border-[#DED8EB] last:border-r-0 ${today ? 'text-[#6B3FA0] bg-[#EDE7F6]' : 'text-[#8E85A0]'}`}>
                    <p>{WEEK_DAYS[date.getDay()]}</p>
                    <p className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{format(date, 'd')}</p>
                  </div>
                );
              })}
            </div>
            <ScrollArea className="max-h-[500px]">
              {filteredGridEmployees.map(emp => {
                // Only count shifts in the visible week, and merge
                // overlapping participant rows so a single time block
                // isn't multiplied by participant/group count.
                const weekDateStrs = weekDates.map(d => format(d, 'yyyy-MM-dd'));
                const empShifts = shiftsForEmployeeInDates(shifts, emp.id, weekDateStrs);
                const weeklyHours = computeMergedHours(empShifts);
                return (
                  <div key={emp.id} className="grid grid-cols-8 border-b border-[#DED8EB] last:border-b-0 hover:bg-[#F7F4EF]/50 transition-colors" data-testid={`grid-row-${emp.id}`}>
                    <div className="px-3 py-2 border-r border-[#DED8EB] flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#9B7ED8] flex items-center justify-center text-white text-[10px] font-bold shrink-0">{emp.name?.charAt(0)}</div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[#2D1B4E] truncate">{emp.name}</p>
                        <p className="text-[10px] text-[#8E85A0]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{weeklyHours.toFixed(0)}h</p>
                      </div>
                    </div>
                    {weekDates.map(date => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const dayShiftsForEmp = empShifts.filter(s => s.date === dateStr && (activeService === 'all' || s.service_line === activeService));
                      return (
                        <div key={dateStr} className="px-1 py-1.5 border-r border-[#DED8EB] last:border-r-0 min-h-[48px]">
                          {dayShiftsForEmp.map(s => (
                            <div key={s.id} onClick={() => openEdit(s)}
                              className={`px-1.5 py-0.5 rounded text-[10px] mb-0.5 cursor-pointer truncate border ${SERVICE_STYLES[s.service_line]} ${s.has_conflict ? 'ring-1 ring-[#F4A261]' : ''}`}
                              title={`${formatTimeRange(s.time_in, s.time_out)} ${(s.participants || []).join(', ')}`}>
                              <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTime(s.time_in)}</span>
                              {s.participants?.length > 0 && <span className="ml-0.5 opacity-75">{s.participants[0]}</span>}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </ScrollArea>
          </div>
        </div>
      )}

      {/* === DIALOGS === */}
      {/* Shift Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto" data-testid="shift-dialog">
          <DialogHeader className="pb-1">
            <DialogTitle className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingShift ? 'Edit Shift' : `Add ${dialogConfig?.label || ''} Shift`}
              {form.group_number && <span className="text-sm font-normal text-[#8E85A0] ml-2">Group {form.group_number}</span>}
            </DialogTitle>
          </DialogHeader>

          {/* Prominent Draft toggle banner — admins/supervisors/schedulers all see this. */}
          <div
            className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border transition-colors ${form.is_draft ? 'bg-[#FFF3E0] border-[#FFB74D]' : 'bg-[#F7F4EF] border-[#DED8EB]'}`}
            data-testid="shift-draft-banner"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-base ${form.is_draft ? 'text-[#E65100]' : 'text-[#8E85A0]'}`}>{form.is_draft ? '📝' : '📅'}</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#2D1B4E] leading-tight">
                  {form.is_draft ? 'Draft — hidden from staff' : 'Published — staff will see this'}
                </p>
                <p className="text-[10px] text-[#8E85A0] leading-tight">Toggle ON to save without notifying staff. Publish later when ready.</p>
              </div>
            </div>
            <Switch checked={!!form.is_draft} onCheckedChange={(v) => setForm(p => ({ ...p, is_draft: !!v }))} data-testid="shift-draft-toggle" />
          </div>

          <div className="space-y-3 py-1">
            {suggestions && !editingShift && (
              <div className={`p-3 rounded-lg border text-sm ${suggestions.still_needed > 0 ? 'bg-[#FFF3E0] border-[#FFB74D] text-[#E65100]' : 'bg-[#E8F5E9] border-[#81C784] text-[#1B5E20]'}`} data-testid="staffing-needs-banner">
                {suggestions.still_needed > 0 ? <><strong>{suggestions.still_needed}</strong> more staff needed ({suggestions.slots_filled}/{suggestions.slots_needed} filled)</> : <>Fully staffed ({suggestions.slots_filled}/{suggestions.slots_needed})</>}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Staff Member</Label>
              {suggestions && !editingShift ? (
                <ScrollArea className="h-40 border border-[#DED8EB] rounded-md">
                  <div className="p-2 space-y-0.5">
                    {suggestions.suggestions.map(s => (
                      <button key={s.id} onClick={() => setForm(p => ({ ...p, employee_id: s.id }))}
                        disabled={s.at_cap || s.already_scheduled}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${
                          form.employee_id === s.id ? 'bg-[#EDE7F6] ring-1 ring-[#6B3FA0]' :
                          s.at_cap || s.already_scheduled ? 'opacity-40 cursor-not-allowed' : !s.available_today ? 'opacity-50' : 'hover:bg-[#F7F4EF]'
                        }`} data-testid={`suggest-${s.id}`}>
                        <div className="w-6 h-6 rounded-full bg-[#9B7ED8] flex items-center justify-center text-white text-[10px] font-bold shrink-0">{s.name?.charAt(0)}</div>
                        <span className="flex-1 truncate text-[#2D1B4E]">{s.name}</span>
                        <span className={`text-[10px] font-mono shrink-0 ${s.near_cap ? 'text-[#E65100] font-semibold' : 'text-[#8E85A0]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.weekly_hours}{s.draft_hours > 0 ? `+${s.draft_hours}d` : ''}/{s.max_weekly_hours}h</span>
                        {s.already_scheduled && <span className="text-[9px] bg-[#D9534F] text-white px-1 rounded">conflict</span>}
                        {s.at_cap && !s.already_scheduled && <span className="text-[9px] bg-[#D9534F] text-white px-1 rounded">cap</span>}
                        {!s.available_today && !s.at_cap && !s.already_scheduled && <span className="text-[9px] bg-[#F4A261] text-white px-1 rounded">off</span>}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <Select value={form.employee_id} onValueChange={(v) => setForm(p => ({ ...p, employee_id: v }))}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="shift-employee-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            {dialogConfig?.fixedTime ? (
              <div className="p-3 rounded-lg bg-[#EDE7F6] border border-[#9B7ED8]">
                <p className="text-sm text-[#2D1B4E]"><Clock className="h-3.5 w-3.5 inline mr-1" />Fixed: <strong style={{ fontFamily: 'IBM Plex Mono, monospace' }}>9:00 AM - 4:00 PM</strong></p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time In</Label><Input type="time" value={form.time_in} onChange={(e) => setForm(p => ({ ...p, time_in: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="shift-time-in" /></div>
                <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time Out</Label><Input type="time" value={form.time_out} onChange={(e) => setForm(p => ({ ...p, time_out: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="shift-time-out" /></div>
              </div>
            )}
            {form.service_line === 'private_pay' && (
              <div className="space-y-2">
                <Label className="text-[#2D1B4E] font-medium">Program</Label>
                <Select value={form.program_id || ''} onValueChange={v => {
                  const prog = privatePayPrograms.find(p => p.id === v);
                  setForm(p => ({
                    ...p,
                    program_id: v,
                    time_in: prog?.default_time_in || p.time_in,
                    time_out: prog?.default_time_out || p.time_out,
                    location: prog?.location || p.location,
                  }));
                }}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="shift-program"><SelectValue placeholder="Select a program" /></SelectTrigger>
                  <SelectContent>
                    {privatePayPrograms.filter(p => p.is_active).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name} · ${(p.price_per_session || 0).toFixed(2)}/session</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Location</Label><Input value={form.location} placeholder="TNS 102, Client home" onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} className="border-[#DED8EB]" data-testid="shift-location" /></div>
            <ParticipantPicker participants={allParticipants} selected={form.participants} onToggle={toggleParticipant} maxCount={dialogSvc === 'day_program' ? 3 : 1} serviceLine={dialogSvc} />
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Notes</Label><Textarea value={form.notes} placeholder="Additional notes..." onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="border-[#DED8EB] min-h-[50px]" data-testid="shift-notes" /></div>
          </div>
          <DialogFooter className="gap-2">
            {editingShift && <Button variant="outline" className="text-[#D9534F] border-[#D9534F] hover:bg-red-50" onClick={handleDelete} data-testid="shift-delete-btn"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
            {editingShift?.is_draft && isSupervisor && (
              <Button onClick={handlePublishOne} disabled={publishing} className="bg-[#1B5E20] hover:bg-[#0D5C12] text-white" data-testid="shift-publish-btn">
                {publishing ? 'Publishing…' : 'Publish'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || (dialogSvc !== 'day_program' && !form.employee_id)} className={`text-white ${form.is_draft ? 'bg-[#FFB74D] hover:bg-[#E65100]' : 'bg-[#6B3FA0] hover:bg-[#5A2F8E]'}`} data-testid="shift-save-btn">{saving ? 'Saving...' : form.is_draft ? (editingShift ? 'Update Draft' : 'Save Draft') : (editingShift ? 'Update' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Week Dialog */}
      <Dialog open={dupeOpen} onOpenChange={setDupeOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="duplicate-week-dialog">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><Copy className="h-5 w-5 text-[#6B3FA0]" /> Duplicate Week</DialogTitle></DialogHeader>
          <div className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${dupeIsDraft ? 'bg-[#FFF3E0] border-[#FFB74D]' : 'bg-[#F7F4EF] border-[#DED8EB]'}`} data-testid="dupe-draft-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-base ${dupeIsDraft ? 'text-[#E65100]' : 'text-[#8E85A0]'}`}>{dupeIsDraft ? '📝' : '📅'}</span>
              <p className="text-sm font-semibold text-[#2D1B4E] leading-tight">{dupeIsDraft ? 'Save copies as Drafts — hidden from staff' : 'Publish copies — staff will see these'}</p>
            </div>
            <Switch checked={!!dupeIsDraft} onCheckedChange={(v) => setDupeIsDraft(!!v)} data-testid="dupe-draft-toggle" />
          </div>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5A4D6E]">Copy all shifts from one week to another.</p>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Copy from (Sunday)</Label><Input type="date" value={dupeSource} onChange={e => setDupeSource(e.target.value)} className="border-[#DED8EB]" data-testid="dupe-source" /></div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Paste to (Sunday)</Label><Input type="date" value={dupeTarget} onChange={e => setDupeTarget(e.target.value)} className="border-[#DED8EB]" data-testid="dupe-target" /></div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Service Line</Label><Select value={dupeService} onValueChange={setDupeService}><SelectTrigger className="border-[#DED8EB]" data-testid="dupe-service"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="day_program">Day Program</SelectItem><SelectItem value="employment">Employment</SelectItem><SelectItem value="in_home">IHCS</SelectItem></SelectContent></Select></div>
            {dupeResult && <div className={`p-3 rounded-lg text-sm ${dupeResult.error ? 'bg-red-50 text-[#D9534F] border border-red-200' : 'bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]'}`} data-testid="dupe-result">{dupeResult.error ? dupeResult.error : `Created ${dupeResult.created} shifts (${dupeResult.skipped} skipped)`}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupeOpen(false)} className="border-[#DED8EB]">Close</Button>
            <Button onClick={handleDuplicateWeek} disabled={duping || !dupeSource || !dupeTarget} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="dupe-submit-btn">{duping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}{duping ? 'Copying...' : 'Duplicate'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slot Dialog */}
      <Dialog open={slotDialogOpen} onOpenChange={setSlotDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="slot-dialog">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{editingSlot ? 'Edit Participant Shift' : 'Add Participant Shift'}</DialogTitle></DialogHeader>
          <div className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${slotForm.is_draft ? 'bg-[#FFF3E0] border-[#FFB74D]' : 'bg-[#F7F4EF] border-[#DED8EB]'}`} data-testid="slot-draft-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-base ${slotForm.is_draft ? 'text-[#E65100]' : 'text-[#8E85A0]'}`}>{slotForm.is_draft ? '📝' : '📅'}</span>
              <p className="text-sm font-semibold text-[#2D1B4E] leading-tight">{slotForm.is_draft ? 'Draft — hidden from staff' : 'Published — staff will see this'}</p>
            </div>
            <Switch checked={!!slotForm.is_draft} onCheckedChange={(v) => setSlotForm(p => ({ ...p, is_draft: !!v }))} data-testid="slot-draft-toggle" />
          </div>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Participant</Label>
              <Select
                value={allParticipants.find(p => (p.name || '').trim() === (slotForm.participant_name || '').trim()) ? slotForm.participant_name : ''}
                onValueChange={v => setSlotForm(p => ({ ...p, participant_name: v }))}
              >
                <SelectTrigger className="border-[#DED8EB]" data-testid="slot-participant-select"><SelectValue placeholder="Select an existing participant" /></SelectTrigger>
                <SelectContent><ScrollArea className="h-48">{allParticipants.map(p => <SelectItem key={p.id || p.name} value={p.name}>{p.name}</SelectItem>)}</ScrollArea></SelectContent>
              </Select>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[10px] uppercase tracking-wider text-[#8E85A0] shrink-0">or type new</span>
                <Input
                  value={slotForm.participant_name}
                  onChange={e => setSlotForm(p => ({ ...p, participant_name: e.target.value }))}
                  placeholder="New participant name (auto-added)"
                  className="border-[#DED8EB] h-8 text-sm"
                  data-testid="slot-participant-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time In</Label><Input type="time" value={slotForm.time_in} onChange={e => setSlotForm(p => ({ ...p, time_in: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="slot-time-in" /></div>
              <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time Out</Label><Input type="time" value={slotForm.time_out} onChange={e => setSlotForm(p => ({ ...p, time_out: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="slot-time-out" /></div>
            </div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Location</Label><Input value={slotForm.location} placeholder="Job site, client home, etc." onChange={e => setSlotForm(p => ({ ...p, location: e.target.value }))} className="border-[#DED8EB]" data-testid="slot-location" /></div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Notes</Label><Input value={slotForm.notes} onChange={e => setSlotForm(p => ({ ...p, notes: e.target.value }))} className="border-[#DED8EB]" data-testid="slot-notes" /></div>
          </div>
          <DialogFooter className="gap-2">
            {editingSlot && <Button variant="outline" className="text-[#D9534F] border-[#D9534F] hover:bg-red-50" onClick={handleDeleteSlot} data-testid="slot-delete-btn"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
            <Button variant="outline" onClick={() => setSlotDialogOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleSaveSlot} disabled={slotSaving || !slotForm.participant_name} className={`text-white ${slotForm.is_draft ? 'bg-[#FFB74D] hover:bg-[#E65100]' : 'bg-[#6B3FA0] hover:bg-[#5A2F8E]'}`} data-testid="slot-save-btn">{slotSaving ? 'Saving...' : slotForm.is_draft ? (editingSlot ? 'Update Draft' : 'Save Draft') : (editingSlot ? 'Update' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pattern Dialog */}
      <Dialog open={patternOpen} onOpenChange={setPatternOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="pattern-dialog">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><CalendarDays className="h-5 w-5 text-[#6B3FA0]" /> Weekly Pattern</DialogTitle></DialogHeader>
          <div className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${patternForm.is_draft ? 'bg-[#FFF3E0] border-[#FFB74D]' : 'bg-[#F7F4EF] border-[#DED8EB]'}`} data-testid="pattern-draft-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-base ${patternForm.is_draft ? 'text-[#E65100]' : 'text-[#8E85A0]'}`}>{patternForm.is_draft ? '📝' : '📅'}</span>
              <p className="text-sm font-semibold text-[#2D1B4E] leading-tight">{patternForm.is_draft ? 'Draft pattern — hidden from staff' : 'Published — staff will see this'}</p>
            </div>
            <Switch checked={!!patternForm.is_draft} onCheckedChange={(v) => setPatternForm(p => ({ ...p, is_draft: !!v }))} data-testid="pattern-draft-toggle" />
          </div>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5A4D6E]">Create recurring schedule for this week ({format(currentWeek, 'MMM d')} - {format(addDays(currentWeek, 6), 'MMM d')}).</p>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Participant</Label>
              <Select
                value={allParticipants.find(p => (p.name || '').trim() === (patternForm.participant_name || '').trim()) ? patternForm.participant_name : ''}
                onValueChange={v => setPatternForm(p => ({ ...p, participant_name: v }))}
              >
                <SelectTrigger className="border-[#DED8EB]" data-testid="pattern-participant"><SelectValue placeholder="Select an existing participant" /></SelectTrigger>
                <SelectContent><ScrollArea className="h-48">{allParticipants.map(p => <SelectItem key={p.id || p.name} value={p.name}>{p.name}</SelectItem>)}</ScrollArea></SelectContent>
              </Select>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[10px] uppercase tracking-wider text-[#8E85A0] shrink-0">or type new</span>
                <Input
                  value={patternForm.participant_name}
                  onChange={e => setPatternForm(p => ({ ...p, participant_name: e.target.value }))}
                  placeholder="New participant name (auto-added)"
                  className="border-[#DED8EB] h-8 text-sm"
                  data-testid="pattern-participant-input"
                />
              </div>
            </div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Days</Label><div className="flex flex-wrap gap-2">{[['sun','Sun'],['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat']].map(([key, label]) => (<button key={key} onClick={() => togglePatternDay(key)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${patternForm.days.includes(key) ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#5A4D6E] border-[#DED8EB] hover:border-[#9B7ED8]'}`} data-testid={`pattern-day-${key}`}>{label}</button>))}</div></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time In</Label><Input type="time" value={patternForm.time_in} onChange={e => setPatternForm(p => ({ ...p, time_in: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="pattern-time-in" /></div>
              <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time Out</Label><Input type="time" value={patternForm.time_out} onChange={e => setPatternForm(p => ({ ...p, time_out: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="pattern-time-out" /></div>
            </div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Location</Label><Input value={patternForm.location} placeholder="Job site, client home" onChange={e => setPatternForm(p => ({ ...p, location: e.target.value }))} className="border-[#DED8EB]" data-testid="pattern-location" /></div>
            {patternResult && <div className={`p-3 rounded-lg text-sm ${patternResult.error ? 'bg-red-50 text-[#D9534F] border border-red-200' : 'bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]'}`} data-testid="pattern-result">{patternResult.error ? patternResult.error : `Created ${patternResult.created} slots for ${patternResult.participant}`}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPatternOpen(false)} className="border-[#DED8EB]">Close</Button>
            <Button onClick={handleCreatePattern} disabled={patternSaving || !patternForm.participant_name || patternForm.days.length === 0} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="pattern-submit-btn">{patternSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}{patternSaving ? 'Creating...' : 'Create Pattern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Staff Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Assign Staff{assignSlot && <span className="text-sm font-normal text-[#8E85A0] ml-2">for {(assignSlot.participants || []).join(', ')}</span>}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {assignSlot && <div className="p-3 rounded-lg bg-[#F0EDF6] border border-[#DED8EB] text-sm text-[#5A4D6E]"><p><strong>{(assignSlot.participants || []).join(', ')}</strong></p><p style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(assignSlot.time_in, assignSlot.time_out)}</p>{assignSlot.location && <p className="flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{assignSlot.location}</p>}</div>}
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Select Staff Member</Label>
              {assignSuggestions ? (
                <ScrollArea className="h-48 border border-[#DED8EB] rounded-md">
                  <div className="p-2 space-y-0.5">
                    {assignSuggestions.suggestions.map(s => (
                      <button key={s.id} onClick={() => setAssignEmpId(s.id)} disabled={s.at_cap || s.already_scheduled}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${assignEmpId === s.id ? 'bg-[#EDE7F6] ring-1 ring-[#6B3FA0]' : s.at_cap || s.already_scheduled ? 'opacity-40 cursor-not-allowed' : !s.available_today ? 'opacity-50' : 'hover:bg-[#F7F4EF]'}`} data-testid={`assign-suggest-${s.id}`}>
                        <div className="w-6 h-6 rounded-full bg-[#9B7ED8] flex items-center justify-center text-white text-[10px] font-bold shrink-0">{s.name?.charAt(0)}</div>
                        <span className="flex-1 truncate text-[#2D1B4E]">{s.name}</span>
                        <span className={`text-[10px] font-mono shrink-0 ${s.near_cap ? 'text-[#E65100] font-semibold' : 'text-[#8E85A0]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.weekly_hours}{s.draft_hours > 0 ? `+${s.draft_hours}d` : ''}/{s.max_weekly_hours}h</span>
                        {s.already_scheduled && <span className="text-[9px] bg-[#D9534F] text-white px-1 rounded">conflict</span>}
                        {s.at_cap && !s.already_scheduled && <span className="text-[9px] bg-[#D9534F] text-white px-1 rounded">cap</span>}
                        {!s.available_today && !s.at_cap && !s.already_scheduled && <span className="text-[9px] bg-[#F4A261] text-white px-1 rounded">off</span>}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <Select value={assignEmpId} onValueChange={setAssignEmpId}><SelectTrigger className="border-[#DED8EB]" data-testid="assign-employee-select"><SelectValue placeholder="Select staff" /></SelectTrigger><SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleAssignStaff} disabled={assigning || !assignEmpId} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="assign-submit-btn">{assigning ? 'Assigning...' : 'Assign'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Day Dialog */}
      <Dialog open={copyDayOpen} onOpenChange={setCopyDayOpen}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="copy-day-dialog">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><Copy className="h-5 w-5 text-[#6B3FA0]" /> Copy Day</DialogTitle></DialogHeader>
          <div className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${copyIsDraft ? 'bg-[#FFF3E0] border-[#FFB74D]' : 'bg-[#F7F4EF] border-[#DED8EB]'}`} data-testid="copy-draft-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-base ${copyIsDraft ? 'text-[#E65100]' : 'text-[#8E85A0]'}`}>{copyIsDraft ? '📝' : '📅'}</span>
              <p className="text-sm font-semibold text-[#2D1B4E] leading-tight">{copyIsDraft ? 'Save copies as Drafts — hidden from staff' : 'Publish copies — staff will see these'}</p>
            </div>
            <Switch checked={!!copyIsDraft} onCheckedChange={(v) => setCopyIsDraft(!!v)} data-testid="copy-draft-toggle" />
          </div>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5A4D6E]">Copy shifts from <strong>{selectedDate && format(new Date(selectedDate + 'T12:00:00'), 'EEE, MMM d')}</strong></p>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Copy to</Label><Input type="date" value={copyTarget} onChange={e => setCopyTarget(e.target.value)} className="border-[#DED8EB]" data-testid="copy-target-date" /></div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Service Line</Label><Select value={copyService} onValueChange={setCopyService}><SelectTrigger className="border-[#DED8EB]" data-testid="copy-service"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="day_program">Day Program</SelectItem><SelectItem value="employment">Employment</SelectItem><SelectItem value="in_home">IHCS</SelectItem></SelectContent></Select></div>
            {copyResult && <div className={`p-3 rounded-lg text-sm ${copyResult.error ? 'bg-red-50 text-[#D9534F] border border-red-200' : 'bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]'}`} data-testid="copy-result">{copyResult.error ? copyResult.error : `Copied ${copyResult.created} shifts`}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDayOpen(false)} className="border-[#DED8EB]">Close</Button>
            <Button onClick={handleCopyDay} disabled={copying || !copyTarget} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="copy-submit-btn">{copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}{copying ? 'Copying...' : 'Copy'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] flex flex-col" data-testid="bulk-add-dialog">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><Users className="h-5 w-5 text-[#6B3FA0]" /> Bulk Add Participant Shifts</DialogTitle></DialogHeader>
          <div className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${bulkForm.is_draft ? 'bg-[#FFF3E0] border-[#FFB74D]' : 'bg-[#F7F4EF] border-[#DED8EB]'}`} data-testid="bulk-draft-banner">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-base ${bulkForm.is_draft ? 'text-[#E65100]' : 'text-[#8E85A0]'}`}>{bulkForm.is_draft ? '📝' : '📅'}</span>
              <p className="text-sm font-semibold text-[#2D1B4E] leading-tight">{bulkForm.is_draft ? 'Drafts — hidden from staff' : 'Published — staff will see these'}</p>
            </div>
            <Switch checked={!!bulkForm.is_draft} onCheckedChange={(v) => setBulkForm(p => ({ ...p, is_draft: !!v }))} data-testid="bulk-draft-toggle" />
          </div>
          <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1">
            <p className="text-sm text-[#5A4D6E]">Select participants for {selectedDate && format(new Date(selectedDate + 'T12:00:00'), 'EEE, MMM d')}.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time In</Label><Input type="time" value={bulkForm.time_in} onChange={e => setBulkForm(p => ({ ...p, time_in: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="bulk-time-in" /></div>
              <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Time Out</Label><Input type="time" value={bulkForm.time_out} onChange={e => setBulkForm(p => ({ ...p, time_out: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="bulk-time-out" /></div>
            </div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Location</Label><Input value={bulkForm.location} placeholder="Job site" onChange={e => setBulkForm(p => ({ ...p, location: e.target.value }))} className="border-[#DED8EB]" data-testid="bulk-location" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label className="text-[#2D1B4E] font-medium">Participants ({bulkSelected.length})</Label>{bulkSelected.length > 0 && <button onClick={() => setBulkSelected([])} className="text-xs text-[#8E85A0] hover:text-[#D9534F]">Clear</button>}</div>
              <Input placeholder="Search..." value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} className="border-[#DED8EB] h-8 text-sm" data-testid="bulk-search" />
              <ScrollArea className="h-40 border border-[#DED8EB] rounded-md">
                <div className="p-2 space-y-0.5">
                  {allParticipants.filter(p => !bulkSearch || p.name.toLowerCase().includes(bulkSearch.toLowerCase())).map(p => (
                    <label key={p.id || p.name} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${bulkSelected.includes(p.name) ? 'bg-[#EDE7F6]' : 'hover:bg-[#F7F4EF]'}`}>
                      <Checkbox checked={bulkSelected.includes(p.name)} onCheckedChange={() => toggleBulkParticipant(p.name)} data-testid={`bulk-pick-${p.name}`} />
                      <span className="text-[#2D1B4E] truncate">{p.name}</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </div>
            {bulkResult && <div className={`p-3 rounded-lg text-sm ${bulkResult.error ? 'bg-red-50 text-[#D9534F] border border-red-200' : 'bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]'}`} data-testid="bulk-result">{bulkResult.error ? bulkResult.error : `Created ${bulkResult.created} slots`}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} className="border-[#DED8EB]">Close</Button>
            <Button onClick={handleBulkCreate} disabled={bulkSaving || bulkSelected.length === 0} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="bulk-submit-btn">{bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}{bulkSaving ? 'Creating...' : `Create ${bulkSelected.length} Slots`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConflictDialog
        conflict={conflictState}
        busy={conflictBusy}
        onCancel={() => setConflictState(null)}
        onConfirm={() => conflictState?.onConfirm?.()}
      />

      <OverrideDialog
        state={overrideState}
        busy={overrideBusy}
        onCancel={() => setOverrideState(null)}
        onConfirm={(reason) => overrideState?.onConfirm?.(reason)}
      />

      <UnassignedShiftsDialog
        open={openShiftsOpen}
        onClose={() => setOpenShiftsOpen(false)}
        shifts={shifts.filter(s => activeService === 'all' || s.service_line === activeService)}
        employees={employees}
        participants={allParticipants}
        api={api}
        onAssigned={fetchData}
        viewLabel={viewMode === 'monthly' ? format(currentMonth, 'MMMM yyyy') : `Week of ${format(currentWeek, 'MMM d')}`}
      />
    </div>
  );
}
